import React from 'react';
import { clsx } from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'heritage' | 'heritage-outline' | 'destructive';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  asChild?: boolean;
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    icon,
    iconPosition = 'left',
    disabled,
    asChild = false,
    children,
    ...props
  }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center rounded-md font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

    const variants: Record<string, string> = {
      primary: 'bg-brand text-white hover:bg-brand-hover focus-visible:ring-brand shadow-sm',
      secondary: 'bg-surface-2 text-text-primary hover:bg-surface-3 focus-visible:ring-brand',
      outline: 'border border-border bg-transparent text-text-primary hover:bg-surface-1 focus-visible:ring-brand',
      ghost: 'text-text-secondary hover:bg-surface-1 focus-visible:ring-brand',
      heritage: 'bg-brand text-white hover:bg-brand-hover focus-visible:ring-brand shadow-sm',
      'heritage-outline': 'border border-border bg-transparent text-text-primary hover:bg-surface-1 focus-visible:ring-brand',
      destructive: 'bg-error text-white hover:bg-error/90 focus-visible:ring-error shadow-sm',
    };

    const sizes: Record<string, string> = {
      sm: 'h-8 px-3 text-sm gap-1.5',
      md: 'h-10 px-4 text-sm gap-2',
      lg: 'h-12 px-6 text-base gap-2.5',
      xl: 'h-14 px-8 text-lg gap-3',
    };

    const widthClass = fullWidth ? 'w-full' : '';

    const renderIcon = (position: 'left' | 'right') => {
      if (!icon || iconPosition !== position) return null;
      return (
        <span className={clsx(
          'flex-shrink-0',
          loading && 'opacity-0'
        )}>
          {icon}
        </span>
      );
    };

    const childGap = { sm: 'gap-1.5', md: 'gap-2', lg: 'gap-2.5', xl: 'gap-3' }[size];

    const buttonClasses = clsx(baseClasses, variants[variant], sizes[size], widthClass, className);

    if (asChild) {
      const child = children as React.ReactElement<any>;
      return React.cloneElement(child, {
        className: clsx(buttonClasses, child.props?.className),
        ref,
        ...props
      });
    }

    return (
      <button
        className={buttonClasses}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className="absolute h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {renderIcon('left')}
        <span className={clsx('inline-flex items-center', childGap, loading && 'opacity-0')}>{children}</span>
        {renderIcon('right')}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
